import type { LucideIcon } from "lucide-react";

/** Lifecycle/management actions only — formatting excluded. All ids kebab-case. */
export type ObjectActionId =
  // Navigation
  | "open"
  | "expand"
  | "open-in-new-tab"
  | "locate-on-canvas"
  | "edit"
  | "manage"
  // Organization
  | "rename"
  | "move"
  | "copy-to"
  | "duplicate"
  | "copy"
  | "new-folder"
  | "new-document"
  | "new-canvas"
  | "new-chat"
  | "upload-files"
  | "upload-folder"
  | "add-to-workspace"
  | "in-workspace"
  | "copy-url"
  | "wrap-in-section"
  | "remove-from-canvas"
  | "show-on-canvas"
  | "add-to-my-skills"
  // Sharing & access
  | "share"
  | "add-members"
  | "edit-workspace"
  // Favorites
  | "star"
  | "unstar"
  // File-specific
  | "download"
  | "publish"
  | "unpublish"
  | "version-history"
  // Skill mode
  | "set-auto"
  | "set-manual"
  // Automations
  | "run-now"
  | "pause"
  | "resume"
  | "open-location"
  // Canvas ordering
  | "bring-to-front"
  | "send-to-back"
  // Collaboration
  | "chat-about"
  | "comment"
  | "execute-action-item"
  // Destructive
  | "delete"
  | "delete-wrapper-only"
  | "restore"
  // Bulk-only (multi-select toolbar)
  | "bulk-move"
  | "bulk-delete";

export type ObjectActionGroupKey =
  | "navigation"
  | "create"
  | "organization"
  | "sharing"
  | "favorites"
  | "file"
  | "canvas-order"
  | "collaboration"
  | "destructive"
  | "bulk";

export type CanvasNodeType =
  | "entity"
  | "text-note"
  | "canvas-text"
  | "canvas-shape"
  | "section"
  | "widget-node"
  | "chat-session"
  | "canvas-connector"
  | "canvas-drawing";

export type ObjectKind =
  | { type: "workspace"; scope: "team" | "private" }
  | { type: "canvas" }
  | { type: "chat" }
  | {
      type: "file-record";
      class: "workspace" | "canvas" | "flow-session";
      isFolder: boolean;
      /** True for reference rows (kind === "link") — rows that point at a
       *  workspace file rather than holding their own content. */
      isReference?: boolean;
    }
  | { type: "connector" }
  /** Canvas/chat folder (the shared `folders` table), not a file-record folder. */
  | { type: "skill" }
  | { type: "skill-template" }
  | { type: "automation" }
  | {
      type: "canvas-node";
      nodeType: CanvasNodeType;
      entityBase?: string;
      /** For file-record entity nodes: true when the backing row is a
       *  reference to a workspace file (kind === "link"). */
      entityIsReference?: boolean;
    }
  | { type: "canvas-edge" }
  | {
      type: "multi-selection";
      kinds: Array<"canvas" | "chat" | "file" | "folder">;
    };

export type ObjectKindKey = string;

export type ActionSurface =
  | "left-rail"
  | "main-view"
  | "context-menu"
  | "floating-toolbar"
  | "pane-header"
  | "selection-bar"
  | "page-header"
  | "card-overlay";

export const ACTION_SURFACES: readonly ActionSurface[] = [
  "left-rail",
  "main-view",
  "context-menu",
  "floating-toolbar",
  "pane-header",
  "selection-bar",
  "page-header",
  "card-overlay",
] as const;

export type ActionPresentation =
  | "menu-item"
  | "menu-item-with-shortcut"
  | "icon-button"
  | "primary-button"
  | "secondary-button"
  | "corner-star"
  | "inline-edit"
  | "toolbar-primary"
  | "toolbar-more-submenu"
  | "toolbar-utility"
  | "hidden";

export type ActionScope =
  | "canonical"
  | "favorites"
  | "shared-with-me"
  | "canvas-explorer"
  | "trash";

export type TrashPolicy = "confirm" | "undo-toast" | "none";

export interface ActionLabelContext {
  kind: ObjectKind;
  count?: number;
  name?: string;
  isPublished?: boolean;
  isFavorite?: boolean;
}

export interface ObjectActionDefinition {
  id: ObjectActionId;
  label: string | ((ctx: ActionLabelContext) => string);
  /** Static, or kind-sensitive like `label` (e.g. `open` shows a folder glyph
   *  on file folders and the open-in-pane glyph on files). */
  icon: LucideIcon | ((ctx: ActionLabelContext) => LucideIcon);
  destructive?: boolean;
  shortcut?: string;
  group: ObjectActionGroupKey;
  trashPolicy?: TrashPolicy;
}

export type ObjectActionPredicate =
  | { not: ObjectActionPredicate }
  | { all: ObjectActionPredicate[] }
  | { any: ObjectActionPredicate[] }
  | { permission: string }
  | { scope: ActionScope }
  | { surface: ActionSurface }
  | { state: keyof ObjectActionContext["state"] }
  | { kind: Partial<ObjectKindFilter> };

export interface ObjectKindFilter {
  type: ObjectKind["type"];
  scope?: "team" | "private";
  isFolder?: boolean;
  class?: "workspace" | "canvas" | "flow-session";
  isReference?: boolean;
  nodeType?: CanvasNodeType;
  entityBase?: string;
  entityIsReference?: boolean;
}

export interface ObjectActionEntry {
  id: ObjectActionId;
  surfaces: readonly ActionSurface[];
  presentation?:
    | ActionPresentation
    | Partial<Record<ActionSurface, ActionPresentation>>;
  hiddenWhen?: ObjectActionPredicate;
  disabledWhen?: ObjectActionPredicate;
  /**
   * Expand this action into runtime variants (e.g. file-record export formats).
   * See {@link attachFileRecordDownloadVariants}.
   */
  variantSource?: "file-record-export";
}

export interface ObjectActionVariant {
  id: string;
  label: string;
  disabled?: boolean;
  handler: () => void;
}

/** A picked transfer target: a workspace plus a folder path within it. */
export interface TransferDestination {
  workspaceId: string;
  /** Folder prefix within the workspace — `""` is the root; otherwise ends with `"/"`. */
  folderPath: string;
  /** Human-readable target (e.g. `Marketing / GTM`) for toasts. */
  label: string;
}

/**
 * Inline destination picker attached to a transfer action (Move to / Copy
 * to). Renderers that support it (dropdown + command menus) show a cascading
 * workspace → folder submenu instead of invoking `handler`; surfaces that
 * can't host a cascade fall back to `handler` (the legacy modal workflow).
 */
export interface ObjectActionDestinationSubmenu {
  kind: "workspace-destination";
  /** Commit semantics — drives labels and toasts. */
  operation: "copy" | "move" | "add-to-workspace";
  /** Run the transfer. Handles its own errors/toasts; safe to fire-and-forget. */
  commit: (destination: TransferDestination) => void;
  /** Workspace the record currently lives in (workspace-class only). */
  currentWorkspaceId?: string;
  /**
   * The record's current parent folder path within `currentWorkspaceId`
   * (`""` = root). Marks that target as a disabled no-op for moves.
   */
  currentFolderPath?: string;
  /**
   * Folder subtrees in `currentWorkspaceId` that can't be targeted — a
   * folder can't move into itself or a descendant.
   */
  forbiddenPathPrefixes?: readonly string[];
  /**
   * Workspaces the actor may write to. A destination outside this set is
   * rendered disabled rather than hidden, so a workspace you can see but
   * not write to reads as "not allowed" instead of quietly missing.
   * Undefined means the check has not resolved yet.
   */
  writableWorkspaceIds?: ReadonlySet<string>;
  /**
   * Limit the workspace list — Add to workspace is locked to the container's
   * own workspace (promotion never crosses workspaces, A3), so its cascade
   * offers exactly one. Undefined = every workspace. An empty array renders
   * the loading/empty row (the home workspace hasn't resolved yet).
   */
  restrictToWorkspaceIds?: readonly string[];
  /** Canvas-folder ids that can't be targeted — the folder itself and its subtree. */
  forbiddenFolderIds?: ReadonlySet<string>;
}

export interface ObjectTypeConfig {
  /** Merge actions from another manifest kind (child overrides by id). */
  extends?: ObjectKindKey;
  actions: readonly ObjectActionEntry[];
}

export type ObjectActionManifest = Record<ObjectKindKey, ObjectTypeConfig>;

export interface ObjectActionContext {
  surface: ActionSurface;
  kind: ObjectKind;
  permissions: Record<string, boolean>;
  scope: ActionScope;
  state: {
    isArchived?: boolean;
    isPublished?: boolean;
    isFavorite?: boolean;
    canEdit?: boolean;
    canShare?: boolean;
    canDelete?: boolean;
    canMove?: boolean;
    selectionCount?: number;
    selectionSingle?: boolean;
    canRenameSelection?: boolean;
    canDuplicateSelection?: boolean;
    hasChatsInSelection?: boolean;
    /** Multi-selection contains at least one container file that can promote. */
    hasPromotableFiles?: boolean;
    hasWebLink?: boolean;
    hasDetailView?: boolean;
    copyDisabled?: boolean;
    zOrderDisabled?: boolean;
    isPrivateWorkspace?: boolean;
    /** File-record on canvas toolbar / context menu (not workspace list menus). */
    onCanvasDetail?: boolean;
    /** The file's canvas node has `hidden: true` — drives Hide vs Show on canvas. */
    hiddenOnCanvas?: boolean;
    showPublish?: boolean;
    showVersionHistory?: boolean;
    /** Container (workspace/folder) surfaced where files can be uploaded (files tab). */
    /** Skill is in "auto" mode (enabled) — drives set-auto/set-manual visibility. */
    isSkillEnabled?: boolean;
    /** Skill template can be unpublished by the current user (publisher/org admin). */
    canUnpublish?: boolean;
    /** Automation has a next run scheduled — drives pause/resume visibility. */
    isAutomationActive?: boolean;
    /**
     * The record is an HTML block. It opens, shares, publishes and promotes
     * like a document, and additionally offers Expand — the canvas lightbox.
     * Surfaces derive it through `isCanvasIntrinsicRecord` so every menu
     * answers the same question.
     */
    isCanvasIntrinsic?: boolean;
  };
}

export interface ResolvedObjectAction {
  id: ObjectActionId;
  definition: ObjectActionDefinition;
  presentation: ActionPresentation;
  disabled: boolean;
  hidden: boolean;
  handler: () => void;
  /** Populated at runtime for compound actions (e.g. download export formats). */
  variants?: ObjectActionVariant[];
  /** Populated at runtime for transfer actions — inline destination picker. */
  submenu?: ObjectActionDestinationSubmenu;
}

export type ObjectActionHandlers = Partial<Record<ObjectActionId, () => void>>;

export interface StaticActionShell {
  id: ObjectActionId;
  definition: ObjectActionDefinition;
  presentation: ActionPresentation;
  hiddenWhen?: ObjectActionPredicate;
  disabledWhen?: ObjectActionPredicate;
}

export function objectKindKey(kind: ObjectKind): ObjectKindKey {
  switch (kind.type) {
    case "workspace":
      return kind.scope === "private" ? "workspace:private" : "workspace";
    case "canvas":
      return "canvas";
    case "chat":
      return "chat";
    case "file-record":
      return kind.isFolder ? "file-record:folder" : "file-record";
    case "connector":
      return "connector";
    case "skill":
      return "skill";
    case "skill-template":
      return "skill-template";
    case "automation":
      return "automation";
    case "canvas-node":
      return `canvas-node:${kind.nodeType}`;
    case "canvas-edge":
      return "canvas-edge";
    case "multi-selection":
      return "multi-selection";
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function resolvePresentation(
  entry: ObjectActionEntry,
  surface: ActionSurface,
): ActionPresentation {
  if (!entry.presentation) {
    return "menu-item";
  }
  if (typeof entry.presentation === "string") {
    return entry.presentation;
  }
  return entry.presentation[surface] ?? "menu-item";
}
